import React, { useState, useMemo, useEffect } from 'react';
import { AlertTriangle, Calendar, DollarSign, FileText, Users, CheckCircle, Clock, AlertCircle, Edit3, Check, X, Search, SlidersHorizontal } from 'lucide-react';
import Swal from 'sweetalert2';
import { ActionLoader } from '../../../../loader';
import { confirmQuotas, updateUfValue } from '../../../../../services/quotasService';
import { Pagination } from '@mui/material';

const QuotasUnconfirmed = ({ quotas, ufUsed, period, year, reload }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [newUfValue, setNewUfValue] = useState('');
  const [isConfirming, setIsConfirming] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [page, setPage] = useState(1);
  
  // Estados para búsqueda y filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('none'); // none, amount_asc, amount_desc, company_asc, company_desc
  const [showFilters, setShowFilters] = useState(false);
  
  const itemsPerPage = 10;

  // Filtrar y ordenar las cuotas
  const filteredAndSortedQuotas = useMemo(() => {
    let filtered = quotas;

    // Filtrar por término de búsqueda
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      filtered = quotas.filter(quota => 
        quota.company.business_name.toLowerCase().includes(term) ||
        quota.company.rut.toLowerCase().includes(term) ||
        quota.id.toString().includes(term)
      );
    }

    // Ordenar según el criterio seleccionado
    if (sortBy !== 'none') {
      filtered = [...filtered].sort((a, b) => {
        switch (sortBy) {
          case 'amount_desc':
            return parseFloat(b.amount) - parseFloat(a.amount);
          case 'amount_asc':
            return parseFloat(a.amount) - parseFloat(b.amount);
          case 'company_asc':
            return a.company.business_name.localeCompare(b.company.business_name);
          case 'company_desc':
            return b.company.business_name.localeCompare(a.company.business_name);
          default:
            return 0;
        }
      });
    }

    return filtered;
  }, [quotas, searchTerm, sortBy]);

  const totalPages = Math.ceil(filteredAndSortedQuotas.length / itemsPerPage);
  const paginatedQuotas = filteredAndSortedQuotas.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  // Resetear a la página 1 cuando cambien los filtros
  useEffect(() => {
    setPage(1);
  }, [searchTerm, sortBy]);

  // Resetear a la página 1 si cambian las cuotas
  useEffect(() => {
    setPage(1);
  }, [quotas]);

  // Ajustar página si las cuotas disminuyen y la página actual queda fuera de rango
  useEffect(() => {
    if (page > totalPages && totalPages > 0) {
      setPage(totalPages);
    }
  }, [filteredAndSortedQuotas, page, totalPages]);

  // Calcular estadísticas (basadas en las cuotas filtradas)
  const stats = useMemo(() => {
    const totalQuotas = filteredAndSortedQuotas.length;
    const totalAmount = filteredAndSortedQuotas.reduce((sum, quota) => {
      const amount = parseFloat(quota.amount) || 0;
      return sum + amount;
    }, 0);
    
    return {
      totalQuotas,
      totalAmount: Math.round(totalAmount),
      totalOriginal: quotas.length // Para mostrar el total original
    };
  }, [filteredAndSortedQuotas, quotas]);

  // Función para obtener el valor UF efectivo
  const getEffectiveUfValue = () => {
    if (ufUsed && ufUsed.toString().trim() !== '') {
      return ufUsed.toString();
    }
    
    if (quotas && quotas.length > 0) {
      const quotaWithUf = quotas.find(quota => quota.uf_value && quota.uf_value.toString().trim() !== '');
      if (quotaWithUf) {
        return quotaWithUf.uf_value.toString();
      }
    }
    
    return '';
  };

  // Inicializar newUfValue cuando cambien las props
  useEffect(() => {
    const effectiveUfValue = getEffectiveUfValue();
    setNewUfValue(effectiveUfValue);
  }, [ufUsed, quotas]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getStateConfig = (state) => {
    const configs = {
      pending: {
        label: 'Pendiente',
        color: 'bg-yellow-100 text-yellow-800',
        icon: Clock
      },
      paid: {
        label: 'Pagado',
        color: 'bg-green-100 text-green-800',
        icon: CheckCircle
      },
    };
    return configs[state] || configs.pending;
  };

  const handleConfirmOriginal = async () => {
    const effectiveUfValue = getEffectiveUfValue();
    const confirmation = await Swal.fire({
      title: 'Confirmar UF Original',
      text: `¿Estás seguro de que deseas confirmar las cuotas con el valor de UF original: ${effectiveUfValue}?, al confirmar, las cuotas no podrán ser modificadas. Y se enviarán los correos correspondientes a los socios.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, confirmar',
      cancelButtonText: 'Cancelar',
      customClass: {
        confirmButton: 'bg-[#1BBB55] text-white hover:bg-[#159544] focus:ring-green-500',
        cancelButton: 'bg-[#DC3465] text-white hover:bg-[#C62839] focus:ring-gray-500',
      }
    });
    if (!confirmation.isConfirmed) return;
    try{
      setIsSubmitting(true);
      const response = await confirmQuotas(period, year);
      setIsSubmitting(false);
      if (!response.success){
        Swal.fire({
          title: 'Error al confirmar cuotas',
          text: response.message || 'Ocurrió un error al confirmar las cuotas. Por favor, inténtalo nuevamente más tarde.',
          icon: 'error',
          confirmButtonText: 'Aceptar',
          customClass: {
            confirmButton: 'bg-[#DC3465] text-white hover:bg-[#C6284A] focus:ring-[#C6284A]',
          }
        });
        return;
      }
      Swal.fire({
        title: 'Cuotas Confirmadas',
        text: `Las cuotas han sido confirmadas con el valor de UF original: ${effectiveUfValue}. Los correos han sido enviados a los socios.`,
        icon: 'success',
        confirmButtonText: 'Aceptar',
        customClass: {
          confirmButton: 'bg-[#1BBB55] text-white hover:bg-[#159544] focus:ring-green-500',
        }
      });
      setIsConfirming(true);
      setIsEditing(false);
      setNewUfValue(effectiveUfValue);
      reload();
    } catch (error) {
      Swal.fire({
        title: 'Error al confirmar cuotas',
        text: 'Ocurrió un error al intentar confirmar las cuotas. Por favor, inténtalo nuevamente más tarde.',
        icon: 'error',
        confirmButtonText: 'Aceptar',
        customClass: {
          confirmButton: 'bg-[#DC3465] text-white hover:bg-[#C6284A] focus:ring-[#C6284A]',
        }
      });
      console.error("Error confirming quotas:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmNew = async () => {
    const confirmation = await Swal.fire({
      title: 'Confirmar actualización de UF',
      text: `¿Estás seguro de que deseas actualizar las cuotas con el nuevo valor de UF: ${newUfValue}?, las cuotas se recalcularán.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, actualizar',
      cancelButtonText: 'Cancelar',
      customClass: {
        confirmButton: 'bg-[#1BBB55] text-white hover:bg-[#1aab51] focus:ring-green-500',
        cancelButton: 'bg-[#DC3465] text-white hover:bg-[#C42251] focus:ring-gray-500',
      }
    });
    if (confirmation.isConfirmed){
      try{
        setIsLoading(true);
        setIsSubmitting(true);
        const response = await updateUfValue(period, year, newUfValue);
        setIsLoading(false);
        setIsSubmitting(false);
        if (response.error === true) {
          Swal.fire({
            title: 'Error al actualizar UF',
            text:  'Ocurrió un error al actualizar el valor de la UF. Por favor, inténtalo nuevamente más tarde.',
            icon: 'error',
            confirmButtonText: 'Aceptar',
            customClass: {
              confirmButton: 'bg-[#DC3465] text-white hover:bg-[#C6284A] focus:ring-[#C6284A]',
            }
          });
          return;
        }
        setIsConfirming(true);
        setIsEditing(false);
        setNewUfValue(getEffectiveUfValue());
        reload();
        Swal.fire({
          title: 'UF Actualizada',
          text: `El valor de la UF ha sido actualizado a ${newUfValue}. Las cuotas se recalcularán y deberá volver a revisar si todo está correcto antes de confirmarlas.`,
          icon: 'success',
          confirmButtonText: 'Aceptar',
          customClass: {
            confirmButton: 'bg-[#1BBB55] text-white hover:bg-[#159544] focus:ring-green-500',
          }
        });
      } catch (error) {
        Swal.fire({
          title: 'Error al actualizar UF',
          text: 'Ocurrió un error al intentar actualizar el valor de la UF. Por favor, inténtalo nuevamente más tarde.',
          icon: 'error',
          confirmButtonText: 'Aceptar',
          customClass: {
            confirmButton: 'bg-[#DC3465] text-white hover:bg-[#C6284A] focus:ring-[#C6284A]',
          }
        });
        console.error("Error updating UF value:", error);
      } finally{
        setIsSubmitting(false);
      }
    } else {
      setIsEditing(false);
      setNewUfValue(getEffectiveUfValue());
    }
  };

  const handleCancelEdit = () => {
    setNewUfValue(getEffectiveUfValue());
    setIsEditing(false);
  };

  // Función para manejar el cambio del input de UF
  const handleUfInputChange = (e) => {
    const value = e.target.value;
    const regex = /^\d*\.?\d*$/;
    if (regex.test(value) || value === '') {
      setNewUfValue(value);
    }
  };

  // Función para limpiar filtros
  const clearFilters = () => {
    setSearchTerm('');
    setSortBy('none');
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, color = "bg-blue-50 border-blue-200" }) => (
    <div className={`p-4 rounded-lg border ${color}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
        </div>
        <Icon className="h-8 w-8 text-gray-400" />
      </div>
    </div>
  );

  const effectiveUfValue = getEffectiveUfValue();

  return (
    <div className="space-y-6">
      <ActionLoader open={isSubmitting} onClose={() =>{}}></ActionLoader>
      
      {/* Header con alerta */}
      <div className="p-6 bg-yellow-50 border border-yellow-200 rounded-lg">
        <div className="flex items-center space-x-3">
          <AlertTriangle className="h-6 w-6 text-yellow-600" />
          <div>
            <h3 className="text-lg font-semibold text-yellow-800">
              Cuotas Pendientes de Confirmación
            </h3>
            <p className="text-yellow-700">
              Se encontraron {quotas.length} cuotas que requieren confirmación de la UF utilizada
            </p>
          </div>
        </div>
      </div>

      {/* Sección de confirmación de UF */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h4 className="text-lg font-medium text-gray-900 mb-4">Valor UF Utilizado</h4>
        
        {!isEditing ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="text-sm text-gray-600">UF utilizada en el cálculo:</p>
                <p className="text-2xl font-bold text-gray-900">{effectiveUfValue}</p>
                <p className="text-sm text-gray-500">
                  Total: {formatCurrency(stats.totalAmount)}
                </p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleConfirmOriginal}
                disabled={isConfirming}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Check className="w-4 h-4 inline mr-2" />
                {isConfirming ? 'Confirmando...' : 'Confirmar UF y Enviar Correos'}
              </button>
              
              <button
                onClick={() => setIsEditing(true)}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              >
                <Edit3 className="w-4 h-4 inline mr-2" />
                Cambiar Valor UF
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center space-x-4">
              <div className="flex-1">
                <label htmlFor="new-uf-value" className="block text-sm font-medium text-gray-700 mb-2">
                  Nuevo valor UF
                </label>
                <input
                  id="new-uf-value"
                  type="text"
                  inputMode="decimal"
                  value={newUfValue}
                  onChange={handleUfInputChange}
                  onKeyDown={(e) => {
                    if ([8, 9, 27, 13, 46, 35, 36, 37, 39].indexOf(e.keyCode) !== -1 ||
                        (e.keyCode === 65 && e.ctrlKey === true) ||
                        (e.keyCode === 67 && e.ctrlKey === true) ||
                        (e.keyCode === 86 && e.ctrlKey === true) ||
                        (e.keyCode === 88 && e.ctrlKey === true)) {
                      return;
                    }
                    if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105) && e.keyCode !== 190) {
                      e.preventDefault();
                    }
                  }}
                  placeholder="Ej: 37821.50"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  required
                  disabled={isLoading}
                />
                <p className="text-xs text-gray-500 mt-1">
                  UF original: {effectiveUfValue}
                </p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleConfirmNew}
                disabled={
                  isConfirming ||
                  !newUfValue ||
                  parseFloat(newUfValue) === 0 ||
                  parseFloat(newUfValue) === parseFloat(effectiveUfValue)
                }
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Check className="w-4 h-4 inline mr-2" />
                {isConfirming ? 'Actualizando...' : 'Actualizar UF'}
              </button>
              
              <button
                onClick={handleCancelEdit}
                className="flex-1 px-4 py-2 bg-[#DC3465] text-white rounded-md hover:bg-[#C6284A] focus:outline-none focus:ring-2 focus:ring-[#C6284A] focus:ring-offset-2 transition-colors"
              >
                <X className="w-4 h-4 inline mr-2" />
                Cancelar
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Tarjetas de resumen */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <StatCard
          title="Total Cuotas"
          value={stats.totalQuotas}
          subtitle={stats.totalOriginal !== stats.totalQuotas ? `de ${stats.totalOriginal} totales` : undefined}
          icon={FileText}
          color="bg-blue-50 border-blue-200"
        />
        <StatCard
          title="Monto Total"
          value={formatCurrency(stats.totalAmount)}
          subtitle={`UF: ${effectiveUfValue}`}
          icon={DollarSign}
          color="bg-green-50 border-green-200"
        />
      </div>


      {/* Barra de búsqueda y filtros */}
      <div className="bg-white p-4 rounded-lg border border-gray-200">
        <div className="flex flex-col lg:flex-row gap-4">
          
          {/* Buscador */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Buscar por empresa, RUT o ID de cuota..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              />
            </div>
          </div>

          {/* Filtros */}
          <div className="flex flex-col sm:flex-row gap-3">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            >
              <option value="none">Sin ordenar</option>
              <option value="amount_desc">Monto: Mayor a menor</option>
              <option value="amount_asc">Monto: Menor a mayor</option>
              <option value="company_asc">Empresa: A-Z</option>
              <option value="company_desc">Empresa: Z-A</option>
            </select>

            {(searchTerm || sortBy !== 'none') && (
              <button
                onClick={clearFilters}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
              >
                Limpiar filtros
              </button>
            )}
          </div>
        </div>

        {/* Información de resultados */}
        {(searchTerm || sortBy !== 'none') && (
          <div className="mt-3 text-sm text-gray-600">
            Mostrando {filteredAndSortedQuotas.length} de {quotas.length} cuotas
            {searchTerm && ` que coinciden con "${searchTerm}"`}
          </div>
        )}
      </div>

      {/* Tabla de cuotas */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden flex flex-col h-full">
        
        {/* Cabecera de la tarjeta */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h4 className="text-lg font-medium text-gray-900">Detalle de Cuotas</h4>
        </div>

        {/* Contenedor scroll interno */}
        <div className="flex-1 overflow-auto">
          <table className="min-w-full divide-y divide-gray-200">
            
            <thead className="bg-gray-50 sticky top-0 z-10 shadow-sm">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Documento
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Empresa
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Período
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Monto (CLP)
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
              </tr>
            </thead>

            <tbody className="bg-white divide-y divide-gray-200">
              {filteredAndSortedQuotas.length > 0 ? (
                paginatedQuotas.map((quota) => {
                  const stateConfig = getStateConfig(quota.state);
                  const StateIcon = stateConfig.icon;
                  const quotaAmount = parseFloat(quota.amount) || 0;

                  return (
                    <tr key={quota.id} className="hover:bg-gray-100 cursor-pointer transition-colors">
                      
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {quota.id}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap max-w-[200px] truncate" title={quota.company.business_name}>
                        <div className="text-sm font-medium text-gray-900">
                          {quota.company.business_name}
                        </div>
                        <div className="text-sm text-gray-500">{quota.company.rut}</div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {quota.period} {quota.year}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {formatCurrency(Math.round(quotaAmount))}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${stateConfig.color}`}>
                          <StateIcon className="w-3 h-3 mr-1" />
                          {stateConfig.label}
                        </span>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center">
                    <div className="text-gray-500">
                      <div className="text-3xl mb-3">
                        {searchTerm || sortBy !== 'none' ? '🔍' : '📄'}
                      </div>
                      <div className="text-sm font-medium mb-1">
                        {searchTerm || sortBy !== 'none' 
                          ? 'No se encontraron cuotas que coincidan con los filtros'
                          : 'No hay cuotas registradas'
                        }
                      </div>
                      {(searchTerm || sortBy !== 'none') && (
                        <button
                          onClick={clearFilters}
                          className="text-blue-600 hover:text-blue-800 text-sm mt-2"
                        >
                          Limpiar filtros
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          
          {totalPages > 1 && (
            <div className="flex justify-center mt-4 md:mt-5">
              <Pagination
                count={totalPages}
                page={page}
                onChange={(event, value) => setPage(value)}
                color="primary"
                siblingCount={1}
                className="scale-90 md:scale-100"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuotasUnconfirmed;